import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import IndustryIcon from './IndustryIcons';
import { withAlpha } from '../utils/color';

// Each industry has its own jewel tone (chosen so no two neighbouring cards,
// across or down, look alike). `color` drives the illustration and its tile.
const industries = [
  { icon: 'retail', color: '#E0457B', name: 'Retail & E-Commerce', desc: 'Storefronts, inventory systems, and checkout experiences built to convert.' },
  { icon: 'finance', color: '#10A37F', name: 'Finance & FinTech', desc: 'Secure payment flows, reporting dashboards, and compliance-ready infrastructure.' },
  { icon: 'manufacturing', color: '#D9861A', name: 'Manufacturing', desc: 'Production tracking, supply chain visibility, and legacy system modernization.' },
  { icon: 'healthcare', color: '#0E9DAA', name: 'Healthcare', desc: 'Patient portals, EHR integrations, and HIPAA-conscious systems that keep care teams moving fast.' },
  { icon: 'realEstate', color: '#5B61E0', name: 'Real Estate', desc: 'Listing platforms, CRM integrations, and virtual tour experiences.' },
  { icon: 'automotive', color: '#2F80D8', name: 'Automotive', desc: 'Dealership CRM workflows, virtual showroom web experiences, and service appointment automation.' },
  { icon: 'logistics', color: '#E27A26', name: 'Logistics', desc: 'Fleet tracking, route optimization, and real-time shipment visibility.' },
  { icon: 'education', color: '#8B5CF6', name: 'Education', desc: 'Learning management systems, student portals, and virtual classrooms.' },
  { icon: 'hospitality', color: '#C29424', name: 'Hospitality & Travel', desc: 'Booking engines, guest experience apps, and loyalty program platforms.' },
  { icon: 'd2c', color: '#C24EB9', name: 'Direct-to-Consumer (D2C)', desc: 'Custom storefronts, WhatsApp-driven customer engagement flows, and post-purchase automation.' },
];

function IndustryCard({ item, colors, theme }) {
  const [hovered, setHovered] = useState(false);
  const dark = theme === 'dark';
  
  return (
    <div
      className="industry-card"
      style={{
        background: colors.surface,
        border: '1px solid',
        padding: '2rem 1.5rem', 
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
        cursor: 'default',
        borderColor: hovered ? withAlpha(item.color, 0.6) : colors.border,
        transform: hovered ? 'translateY(-4px)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start', // icons line up across the row, whatever the name length
        alignItems: 'center',
        textAlign: 'center',
        minHeight: '220px', // Fixed height ensures the grid stays perfectly aligned
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Full-colour illustration on a softly tinted tile that lifts on hover */}
      <div
        className="industry-icon"
        style={{
          width: '88px',
          height: '88px',
          borderRadius: '24px',
          background: `linear-gradient(145deg, ${withAlpha(item.color, dark ? 0.24 : 0.18)}, ${withAlpha(item.color, dark ? 0.08 : 0.05)})`,
          border: `1px solid ${withAlpha(item.color, hovered ? 0.55 : 0.18)}`,
          boxShadow: hovered ? `0 12px 28px ${withAlpha(item.color, 0.28)}` : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
          transform: hovered ? 'translateY(-6px)' : 'none',
        }}>
        <IndustryIcon name={item.icon} color={item.color} size={62} />
      </div>
      
      <h3 className="industry-name" style={{
        fontFamily: "'Inter', sans-serif", 
        fontWeight: 800, 
        fontSize: '1.2rem', 
        color: colors.text, 
        margin: '1rem 0 0.5rem 0' 
      }}>
        {/* Keep short hyphenated words (E-Commerce) from splitting; long ones
            (Direct-to-Consumer) may break at a hyphen on narrow phone tiles. */}
        {item.name.split(' ').map((word, i) => (
          <span key={i}>
            {i > 0 && ' '}
            {word.includes('-') && word.length <= 12 ? <span style={{ whiteSpace: 'nowrap' }}>{word}</span> : word}
          </span>
        ))}
      </h3>
      
      {/* Description smoothly fades in and slides up on hover */}
      <div style={{
        height: hovered ? 'auto' : '0px',
        opacity: hovered ? 1 : 0,
        overflow: 'hidden',
        transition: 'all 0.4s ease',
        marginTop: hovered ? '0.5rem' : '0'
      }}>
        <p style={{ 
          fontSize: '0.85rem', 
          color: colors.muted, 
          lineHeight: 1.6, 
          margin: 0 
        }}>
          {item.desc}
        </p>
      </div>
    </div>
  );
}

export default function Industries() {
  const { colors, theme } = useTheme();
  
  const s = {
    section: { padding: '100px 5vw', background: colors.bg },
    title: {
      fontFamily: "'Inter', sans-serif", fontWeight: 700,
      fontSize: 'clamp(2rem, 4vw, 3.5rem)',
      lineHeight: 1, letterSpacing: '2px', color: colors.heading, marginBottom: '1rem',
      textAlign: 'center',
    },
    desc: { 
      color: colors.muted, fontSize: '1rem', lineHeight: 1.8, 
      maxWidth: '560px', fontWeight: 300, marginBottom: '3rem',
      textAlign: 'center', marginLeft: 'auto', marginRight: 'auto',
    },
    grid: {
      display: 'grid',
      // 10 industries: two even rows of 5 on laptops/desktops, 2 across on
      // tablets and phones — never a part-filled last row.
      gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
      gap: '1.5rem',
    },
  };

  return (
    <section id="industries" style={s.section} className="industries-title-wrap">
      <h2 style={s.title}>INDUSTRIES WE SERVE</h2>
      <p style={s.desc}>
        Deep domain experience across the sectors that need technology partners who
        understand both the code and the business behind it.
      </p>
      
      <div style={s.grid} className="industries-grid">
        {industries.map((item) => (
          <IndustryCard key={item.name} item={item} colors={colors} theme={theme} />
        ))}
      </div>

      <style>{`
        @media (max-width: 1180px) {
          .industries-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        /* Phones: still 2 across, with a compact tile so names fit the
           narrower cards. */
        @media (max-width: 600px) {
          .industries-title-wrap h2 { white-space: normal !important; }
          .industries-grid { gap: 0.75rem !important; }
          .industry-card { padding: 1.25rem 0.75rem !important; min-height: 170px !important; }
          .industry-icon { width: 68px !important; height: 68px !important; border-radius: 18px !important; }
          .industry-icon svg { width: 48px; height: 48px; }
          .industry-name { font-size: 0.95rem !important; margin-top: 0.8rem !important; }
        }
      `}</style>
    </section>
  );
}